import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SkeletonRows } from "../../Components/Common/LoadingStates";
import {
  getAdminUsers,
  getAdminUserById,
  getAdminRoles,
  getAdminPermissions,
  postInviteUser,
  postResendInvite,
  postDeactivateUser,
  patchAdminUser,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";

document.title = "Team | BiorGov Admin";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupPermissions(perms) {
  const map = new Map();
  for (const p of perms) {
    if (!map.has(p.group)) map.set(p.group, []);
    map.get(p.group).push(p);
  }
  return Array.from(map.entries()).map(([group, items]) => ({ group, items }));
}

function rolePermsSet(role) {
  return new Set(role && role.permissions ? role.permissions : []);
}

function computeOverrides(selectedKeys, rolePerms, allPermissions) {
  const overrides = [];
  for (const p of allPermissions) {
    const inRole = rolePerms.has(p.key);
    const isSelected = selectedKeys.has(p.key);
    if (isSelected && !inRole) overrides.push({ permission_id: p.id, granted: true });
    else if (!isSelected && inRole) overrides.push({ permission_id: p.id, granted: false });
  }
  return overrides;
}

// ─── Top-level page ──────────────────────────────────────────────────────────

export default function UsersPage() {
  const meId = useSelector((s) => s.Profile.user && s.Profile.user.id);
  const isSuperAdmin = useSelector((s) => s.Profile.is_super_admin);

  const [error, setError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const usersReq = useCachedRequest({
    key: "users:all",
    fetcher: async () => {
      const [u, r, p] = await Promise.all([getAdminUsers(), getAdminRoles(), getAdminPermissions()]);
      return {
        users: u.users || [],
        roles: r.roles || [],
        permissions: p.permissions || [],
      };
    },
    enabled: isSuperAdmin,
  });
  const users = usersReq.data?.users || [];
  const roles = usersReq.data?.roles || [];
  const permissions = usersReq.data?.permissions || [];
  const loading = usersReq.loading;
  useEffect(() => {
    if (usersReq.error && !usersReq.data) {
      setError(typeof usersReq.error === "string" ? usersReq.error : "Failed to load users.");
    }
  }, [usersReq.error, usersReq.data]);

  const groupedPerms = useMemo(() => groupPermissions(permissions), [permissions]);

  async function loadAll() {
    invalidateCache("users:");
    await usersReq.refetch();
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <p style={{ color: "var(--bl-text-soft)" }}>Only super-admins can manage users.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(1rem, 3vw, 1.75rem)", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1rem",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1.5rem"
      }}>
        <div>
          <h1 className="bl-gradient-text" style={{
            margin: 0,
            fontFamily: "var(--bl-font-display)",
            fontSize: "clamp(1.5rem, 4vw, 2rem)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}>Team</h1>
          <p style={{ marginTop: 4, color: "var(--bl-text-soft)", fontSize: 14 }}>
            Invite teammates and manage their permissions.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={loadAll}
            disabled={loading}
            className="bl-btn-secondary"
            title="Reload the user list"
            style={{ opacity: loading ? 0.6 : 1 }}
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} style={{ marginRight: 6 }} />
            Refresh
          </button>
          <button onClick={() => setInviteOpen(true)} className="bl-btn-primary">
            <i className="ri-user-add-line" style={{ marginRight: 6 }} />
            Invite user
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{error}</div>}

      <div className="bl-glass" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: "rgba(var(--bl-accent-rgb), 0.06)", borderBottom: "1px solid var(--bl-line-strong)" }}>
                {["Email", "Name", "Role", "Status", "Last login", ""].map((h, i) => (
                  <th key={i} style={{
                    padding: "0.75rem 1rem",
                    textAlign: i === 5 ? "right" : "left",
                    fontSize: 11,
                    color: "var(--bl-text-soft)",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <SkeletonRows rows={4} cols={6} />}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} style={{ padding: "1.5rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
                  No users yet — invite one to get started.
                </td></tr>
              )}
              {!loading && users.map((u) => {
                const isPending = !u.is_active;
                const isSelf = u.id === meId;
                return (
                  <tr key={u.id} style={{ borderBottom: "1px solid var(--bl-line)" }}>
                    <td style={{ padding: "0.85rem 1rem", color: "#fff" }}>{u.email}</td>
                    <td style={{ padding: "0.85rem 1rem", color: "var(--bl-text-strong)" }}>{u.name || <span style={{ color: "var(--bl-text-faint)" }}>—</span>}</td>
                    <td style={{ padding: "0.85rem 1rem" }}>
                      {u.is_super_admin ? (
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11,
                          background: "rgba(188, 126, 255, 0.16)", color: "#bc7eff",
                        }}>Super-admin</span>
                      ) : (
                        u.role ? <span style={{ color: "var(--bl-text-strong)" }}>{u.role.name}</span> : <span style={{ color: "var(--bl-text-faint)" }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "0.85rem 1rem" }}>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11,
                        background: isPending ? "rgba(243,175,76,0.16)" : "rgba(var(--bl-success-rgb),0.16)",
                        color: isPending ? "#ffd199" : "var(--bl-success-soft)",
                      }}>
                        {isPending ? "Pending invite" : "Active"}
                      </span>
                    </td>
                    <td style={{ padding: "0.85rem 1rem", color: "var(--bl-text-soft)", fontSize: 13 }}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : <span style={{ color: "var(--bl-text-faint)" }}>Never</span>}
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "right", whiteSpace: "nowrap" }}>
                      {isPending && (
                        <button
                          onClick={async () => {
                            try { await postResendInvite(u.id); alert("Invite resent."); }
                            catch (err) { alert(typeof err === "string" ? err : "Failed to resend invite."); }
                          }}
                          style={inlineLinkBtn}
                        >Resend invite</button>
                      )}
                      {!isPending && !isSelf && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Deactivate ${u.email}? They will be signed out and unable to log in.`)) return;
                            try { await postDeactivateUser(u.id); await loadAll(); }
                            catch (err) { alert(typeof err === "string" ? err : "Failed to deactivate user."); }
                          }}
                          style={{ ...inlineLinkBtn, color: "#ff8a98" }}
                        >Deactivate</button>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            const detail = await getAdminUserById(u.id);
                            setEditingUser(detail.user);
                          } catch (err) {
                            alert(typeof err === "string" ? err : "Failed to load user.");
                          }
                        }}
                        style={inlineLinkBtn}
                      >Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {inviteOpen && (
        <InviteModal
          roles={roles}
          onClose={() => setInviteOpen(false)}
          onCreated={async () => { setInviteOpen(false); await loadAll(); }}
        />
      )}

      {editingUser && (
        <EditUserDrawer
          user={editingUser}
          roles={roles}
          permissions={permissions}
          groupedPerms={groupedPerms}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { setEditingUser(null); await loadAll(); }}
        />
      )}
    </div>
  );
}

const inlineLinkBtn = {
  background: "transparent",
  border: 0,
  color: "var(--bl-accent-light)",
  fontSize: 13,
  cursor: "pointer",
  marginLeft: 8,
  padding: "4px 6px",
  borderRadius: 6,
  textDecoration: "underline",
  textUnderlineOffset: 2,
};

// ─── Invite modal ────────────────────────────────────────────────────────────

function InviteModal({ roles, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState(roles.find((r) => r.key === "viewer")?.id || roles[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await postInviteUser({ email, name: name || null, role_id: Number(roleId) || null });
      onCreated();
    } catch (e2) {
      setErr(typeof e2 === "string" ? e2 : "Failed to send invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="bl-glass-strong" style={{
        maxWidth: 460, width: "100%", padding: "1.5rem", margin: "1rem"
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, marginBottom: "1rem", fontFamily: "var(--bl-font-display)", fontSize: "1.2rem", color: "#fff" }}>Invite user</h3>
        {err && <div className="alert alert-danger" style={{ marginBottom: "0.75rem" }}>{err}</div>}
        <form onSubmit={submit}>
          <div style={{ marginBottom: "0.85rem" }}>
            <label style={fieldLabelStyle}>Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bl-input" />
          </div>
          <div style={{ marginBottom: "0.85rem" }}>
            <label style={fieldLabelStyle}>Name (optional)</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="bl-input" />
          </div>
          <div style={{ marginBottom: "0.85rem" }}>
            <label style={fieldLabelStyle}>Role</label>
            <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className="bl-input">
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <p style={{ marginTop: 4, fontSize: 12, color: "var(--bl-text-soft)" }}>
              Fine-tune permissions after they accept.
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="button" onClick={onClose} className="bl-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="bl-btn-primary">{busy ? "Sending…" : "Send invite"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit drawer ─────────────────────────────────────────────────────────────

function EditUserDrawer({ user, roles, permissions, groupedPerms, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [roleId, setRoleId] = useState(user.role ? user.role.id : "");
  const [active, setActive] = useState(Boolean(user.is_active));

  const initialEffective = useMemo(() => {
    if (user.is_super_admin) return new Set(permissions.map((p) => p.key));
    return new Set(user.effective_permissions || []);
  }, [user, permissions]);

  const [selected, setSelected] = useState(initialEffective);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const selectedRole = roles.find((r) => r.id === Number(roleId));
  const rolePerms = useMemo(() => rolePermsSet(selectedRole), [selectedRole]);

  function togglePerm(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setErr("");
    try {
      const overrides = computeOverrides(selected, rolePerms, permissions);
      await patchAdminUser(user.id, {
        name,
        role_id: roleId ? Number(roleId) : null,
        is_active: active,
        overrides,
      });
      onSaved();
    } catch (e) {
      setErr(typeof e === "string" ? e : "Failed to save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...overlayStyle, justifyContent: "flex-end" }} onClick={onClose}>
      <div
        className="bl-glass-strong"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 540,
          height: "100vh",
          overflowY: "auto",
          padding: "1.5rem",
          borderRadius: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
          <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.2rem", color: "#fff" }}>Edit user</h3>
          <button onClick={onClose} aria-label="Close" style={{
            background: "transparent", border: 0, color: "var(--bl-text-soft)", fontSize: 20, cursor: "pointer", padding: 6,
          }}>✕</button>
        </div>
        <div style={{ color: "var(--bl-text-soft)", fontSize: 13, marginBottom: "1rem" }}>{user.email}</div>

        {err && <div className="alert alert-danger" style={{ marginBottom: "0.75rem" }}>{err}</div>}

        {user.is_super_admin && (
          <div className="alert alert-info" style={{ marginBottom: "1rem" }}>
            This user is a super-admin — they have all permissions implicitly. Demotion isn't supported in this view.
          </div>
        )}

        <div style={{ marginBottom: "0.85rem" }}>
          <label style={fieldLabelStyle}>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="bl-input" />
        </div>
        <div style={{ marginBottom: "0.85rem" }}>
          <label style={fieldLabelStyle}>Role</label>
          <select disabled={user.is_super_admin} value={roleId} onChange={(e) => setRoleId(e.target.value)} className="bl-input">
            <option value="">— No role —</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.25rem", cursor: "pointer" }}>
          <input type="checkbox" id="is_active" checked={active} onChange={(e) => setActive(e.target.checked)}
            style={{ accentColor: "var(--bl-accent)" }} />
          <span style={{ fontSize: 13, color: "var(--bl-text-strong)" }}>Account active</span>
        </label>

        <div style={{ marginBottom: "0.5rem" }}>
          <h4 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1rem", color: "#fff" }}>Permissions</h4>
          <p style={{ marginTop: 4, marginBottom: "0.85rem", fontSize: 12, color: "var(--bl-text-soft)" }}>
            Defaults come from the role. Tick to grant, untick to deny.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            {groupedPerms.map((g) => (
              <div key={g.group} style={{
                border: "1px solid var(--bl-line)",
                borderRadius: "0.85rem",
                padding: "0.85rem",
                background: "rgba(255,255,255,0.02)",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: "var(--bl-text-soft)",
                  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem",
                }}>{g.group}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {g.items.map((p) => {
                    const checked = user.is_super_admin || selected.has(p.key);
                    const fromRole = rolePerms.has(p.key);
                    const isOverride = !user.is_super_admin && (checked !== fromRole);
                    return (
                      <label key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: user.is_super_admin ? "not-allowed" : "pointer" }}>
                        <input
                          type="checkbox"
                          disabled={user.is_super_admin}
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          style={{ accentColor: "var(--bl-accent)", marginTop: 2 }}
                        />
                        <span style={{ flex: 1 }}>
                          <span style={{ color: "var(--bl-text-strong)" }}>{p.name}</span>
                          <span style={{ marginLeft: 8, fontSize: 11, color: "var(--bl-text-faint)" }}>{p.key}</span>
                          {isOverride && (
                            <span style={{
                              marginLeft: 8, fontSize: 11, padding: "1px 6px", borderRadius: 4,
                              background: checked ? "rgba(var(--bl-success-rgb),0.14)" : "rgba(255,93,108,0.14)",
                              color: checked ? "var(--bl-success-soft)" : "#ff8a98",
                            }}>{checked ? "Granted" : "Denied"}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem", position: "sticky", bottom: 0, background: "rgba(14,13,41,0.85)", paddingTop: "0.75rem", paddingBottom: "0.5rem" }}>
          <button onClick={onClose} className="bl-btn-secondary">Cancel</button>
          <button onClick={save} disabled={busy} className="bl-btn-primary">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 1050,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const fieldLabelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--bl-text-strong)",
  marginBottom: 6,
};
